import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { useSelector } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import { makeStyles } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import { money } from 'utils';
import { Alert } from '@material-ui/lab';
import useAlert from 'hooks/useAlert';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.paper,
  },
  saveButton: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    '& > button': {
      marginRight: theme.spacing(2),
    },
    '& > button:last-child': {
      marginRight: 0,
    },
  },
  sectionTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },

  preferenceItem: {
    marginBottom: theme.spacing(2),
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    color: 'rgba(0, 0, 0, 0.77)',
    position: 'relative',
    paddingLeft: '40px',
    '&:before': {
      content: '""',
      position: 'absolute',
      left: '10px',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '18px',
      height: '18px',
      borderRadius: '50%',
      border: '2px solid currentColor',
      boxSizing: 'border-box',
    },
    '&.Mui-selected': {
      backgroundColor: '#f0f0f0',
      color: 'rgba(0, 0, 0, 0.87)',
      fontWeight: 600,
      '&:before': {
        border: '2px solid currentColor',
      },
      '&:after': {
        content: '""',
        position: 'absolute',
        left: '14px',
        top: '50%',
        transform: 'translateY(-50%)',
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        backgroundColor: 'currentColor',
      },
    },
    '&.Mui-selected:hover': {
      backgroundColor: '#f0f0f0',
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  preferenceGroup: {
    marginBottom: theme.spacing(3),
  },
  preferenceTitle: {
    marginBottom: theme.spacing(1),
  },
  preferenceSubtitle: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  preferenceList: {
    padding: 0,
  },
  clearPreferencesButton: {
    marginTop: theme.spacing(2),
  },
}));

const PreferencesTab = () => {
  const { globalOptions } = useSelector((s) => s.lab);
  const classes = useStyles();
  const [preferences, setPreferences] = useState({});
  const { enqueueSnackbar } = useSnackbar();
  const alert = useAlert();
  const onPreferenceChange = (optionId, choiceId) => {
    const newPreferences = {
      ...preferences,
      [optionId]: choiceId,
    };
    setPreferences(newPreferences);
    localStorage.setItem('userPreferences', JSON.stringify(newPreferences));
    enqueueSnackbar('Preference saved!', {
      variant: 'success',
    });
  };

  const onClearPreferences = () => {
    alert.confirm({
      title: 'Clear Preferences',
      description: 'Are you sure you want to clear all your saved preferences?',
      onConfirm: () => {
        localStorage.removeItem('userPreferences');
        setPreferences({});
        enqueueSnackbar('Preferences cleared successfully!', {
          variant: 'success',
        });
      },
    });
  };
  useEffect(() => {
    const savedPreferences = localStorage.getItem('userPreferences');
    if (savedPreferences) {
      setPreferences(JSON.parse(savedPreferences));
    }
  }, []);

  return (
    <Box>
      <Typography variant="h6" className={classes.sectionTitle}>
        Preferences
      </Typography>
      <Typography variant="body1" color="textSecondary">
        Set your default preferences for required checkout options. These will
        be automatically selected when you place orders with this device and
        browser (selection is stored in the browser).
      </Typography>

      {globalOptions.global_checkout_options?.filter(
        (option) => option.required
      ).length > 0 ? (
        <Box mt={3}>
          {globalOptions.global_checkout_options
            .filter((option) => option.required)
            .map((option) => (
              <Box key={option.id} className={classes.preferenceGroup}>
                <Typography variant="h6" className={classes.preferenceTitle}>
                  {option.description}
                </Typography>
                {option.subtitle && (
                  <Typography
                    variant="body2"
                    className={classes.preferenceSubtitle}
                  >
                    {option.subtitle}
                  </Typography>
                )}

                <List className={classes.preferenceList}>
                  {option.choices.map((choice) => {
                    const pcontotal = choice.pcontotal
                      ? ` + ${choice.pcontotal}% of cart total`
                      : '';
                    const label =
                      choice.label +
                      ' - ' +
                      money(choice.price, globalOptions.currency) +
                      (choice.priceperfile ? ' per file' : '') +
                      pcontotal;

                    return (
                      <ListItem
                        key={choice.id}
                        button
                        selected={preferences[option.id] === choice.id}
                        onClick={() => onPreferenceChange(option.id, choice.id)}
                        className={classes.preferenceItem}
                        disableRipple
                      >
                        <ListItemText primary={label} />
                      </ListItem>
                    );
                  })}
                  {preferences[option.id] && (
                    <ListItem
                      button
                      onClick={() => onPreferenceChange(option.id, null)}
                      className={classes.preferenceItem}
                      style={{ fontStyle: 'italic', color: '#666' }}
                    >
                      <ListItemText primary="Clear selection" />
                    </ListItem>
                  )}
                </List>
              </Box>
            ))}

          {Object.keys(preferences).length > 0 && (
            <Box className={classes.clearPreferencesButton}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={onClearPreferences}
                size="large"
              >
                Clear All Preferences
              </Button>
            </Box>
          )}
        </Box>
      ) : (
        <Box mt={3}>
          <Typography variant="body2" color="textSecondary">
            No required checkout options are currently available for this lab.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PreferencesTab;
